import { useKeyPress } from 'ahooks';
import { useNavigate as useReactRouterNavigate } from 'react-router';
import {
	RightSidebarMode,
	rightSidebarStore,
} from '../components/RightSidebar';
import { sideBarStore } from '../components/SecodaAppShell/SideBar/store';
import { GLOBAL_SHORTCUTS } from '../constants/shortcuts';
import { useSafeKeyPress } from './useSafeKeyPress';

/**
 * Check if an input element is currently focused to prevent shortcuts during typing
 */
export function isInputFocused(): boolean {
	const { activeElement } = document;
	if (!activeElement) return false;

	const tagName = activeElement.tagName.toLowerCase();
	const isContentEditable =
		activeElement.getAttribute('contenteditable') === 'true';
	const isInput = ['input', 'textarea', 'select'].includes(tagName);

	// Check for common editor classes/attributes
	const isEditor =
		activeElement.closest('[contenteditable="true"]') ||
		activeElement.closest('.ProseMirror') ||
		activeElement.closest('.editor') ||
		activeElement.closest('[role="textbox"]');

	return isInput || isContentEditable || !!isEditor;
}

/**
 * Check if the right sidebar component is currently rendered on the page
 */
function isRightSidebarRendered(): boolean {
	// Look for the RightSidebarWrapper component in the DOM
	return !!document.querySelector('[data-testid="page-sidebar-wrapper"]');
}

/**
 * Custom hook that sets up keyboard shortcuts for toggling sidebars
 * - [ key toggles the left main sidebar
 * - ] key toggles the right entity sidebar
 * - Cmd/Ctrl + Shift + O opens a new AI chat
 *
 * Shortcuts are disabled when typing in inputs, textareas, or editors
 *
 * This should only be called once at the app level to prevent conflicts
 */
export function useSidebarShortcuts() {
	const navigate = useReactRouterNavigate();

	// Left sidebar toggle with [ key (also handles cmd+[ and ctrl+[)
	useSafeKeyPress(
		GLOBAL_SHORTCUTS.LEFT_SIDEBAR_TOGGLE,
		() => {
			sideBarStore.toggle();
		},
		() => isInputFocused()
	);

	// Right sidebar toggle with ] key (also handles cmd+] and ctrl+])
	useSafeKeyPress(
		GLOBAL_SHORTCUTS.RIGHT_SIDEBAR_TOGGLE,
		() => {
			const { collapsed, mode, setCollapsed, setMode } = rightSidebarStore;
			if (collapsed) {
				setCollapsed(false);
				setMode(RightSidebarMode.INFO);
			} else if (mode === RightSidebarMode.INFO) {
				// If the mode is already set to this we instead want to close sidebar.
				setCollapsed(true);
			} else {
				// If sidebar is open but in a different mode, switch to INFO mode
				setMode(RightSidebarMode.INFO);
			}
		},
		() => isInputFocused() || !isRightSidebarRendered()
	);

	// New AI chat with Cmd/Ctrl + Shift + O (works even when input is focused)
	useKeyPress('o', (event) => {
		// Check if both mod key (Cmd/Ctrl) and Shift are pressed
		const isModPressed = event.metaKey || event.ctrlKey;
		const isShiftPressed = event.shiftKey;

		if (isModPressed && isShiftPressed) {
			event.preventDefault();
			navigate('/ai/new');
		}
	});
}
